import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { createIcomxBridge } from './bridge';
import {
  IcomxHostCommandPayloadMap,
  IcomxHostCommandType,
  IcomxSecurityRejection,
} from './types';

interface UseIcomxBridgeProps {
  iframeRef: RefObject<HTMLIFrameElement>;
  iframeOrigin?: string | null;
  onSecurityRejection?: (rejection: IcomxSecurityRejection) => void;
  onBridgeError?: (error: Error) => void;
}

export default function useIcomxBridge({
  iframeRef,
  iframeOrigin,
  onSecurityRejection,
  onBridgeError,
}: UseIcomxBridgeProps) {
  const bridge = useMemo(
    () =>
      createIcomxBridge({
        onSecurityRejection,
        onBridgeError,
      }),
    [onSecurityRejection, onBridgeError]
  );

  const [state, setState] = useState(bridge.getSnapshot());

  const refreshContext = useCallback(() => {
    bridge.updateContext({
      iframeOrigin: iframeOrigin ?? null,
      iframeWindow: iframeRef.current?.contentWindow ?? null,
    });
  }, [bridge, iframeOrigin, iframeRef]);

  useEffect(() => {
    return bridge.subscribe(setState);
  }, [bridge]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<unknown>) => {
      bridge.handleMessage(event);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      bridge.destroy();
    };
  }, [bridge]);

  useEffect(() => {
    refreshContext();
  }, [refreshContext]);

  const sendCommand = useCallback(
    <TType extends IcomxHostCommandType>(
      type: TType,
      payload: IcomxHostCommandPayloadMap[TType]
    ) => {
      return bridge.sendCommand(type, payload);
    },
    [bridge]
  );

  const reset = useCallback(
    (reason?: string) => {
      bridge.reset(reason);
    },
    [bridge]
  );

  return {
    ...state,
    refreshContext,
    reset,
    sendCommand,
  };
}